import { CountyData, CropData, countyService } from './countyService';
import axios from 'axios';
import { ImportExportAnalysis, ExportData, ImportData, TradeRegulation } from './importExportService';
import { supabase } from './supabaseClient';

const NCPB_PRICES_URL =
  import.meta.env.VITE_NCPB_PRICES_URL || 'https://www.ncpb.co.ke/';
const SUPABASE_URL = import.meta.env.VITE_SUPABASE_URL;
const SUPABASE_ANON_KEY = import.meta.env.VITE_SUPABASE_ANON_KEY;
const KAMIS_API_BASE_URL =
  import.meta.env.VITE_KAMIS_API_URL ||
  (SUPABASE_URL ? `${String(SUPABASE_URL).replace(/\/$/, '')}/functions/v1/kamis-market` : '');
const KAMIS_EXPORT_URL = 'https://kamis.kilimo.go.ke/site/market?&export=excel';

const parseHTML = (html: string, selector: string): Document => {
  const parser = new DOMParser();
  const doc = parser.parseFromString(html, 'text/html');
  return doc;
};

class WebScrapingService {
  private baseUrl = 'https://api.kilimo.go.ke'; // Example API endpoint (replace with actual)
  private corsProxy = 'https://cors-anywhere.herokuapp.com/'; // CORS proxy for development
  private proxyUrl = 'https://api.allorigins.win/raw?url=';
  private ncpbCache: { data: any[]; timestamp: number } | null = null;

  // Fetch county data from Kenya Agricultural and Livestock Research Organization
  async getAllCountiesReal() {
    throw new Error('County dataset is not connected in this environment. Please use Supabase-backed county profiles instead.');
  }

  async getKamisMarketPricesByCounty(countyName: string, days: number = 14, limit: number = 100) {
    if (!KAMIS_API_BASE_URL || !SUPABASE_ANON_KEY) {
      return { rows: [], meta: { source: 'kamis.kilimo.go.ke', rows: 0 } };
    }

    const url = new URL(KAMIS_API_BASE_URL);
    url.searchParams.set('county', countyName);
    url.searchParams.set('days', String(days));
    url.searchParams.set('limit', String(limit));
    url.searchParams.set('_ts', String(Date.now()));

    const res = await fetch(url.toString(), {
      headers: {
        apikey: SUPABASE_ANON_KEY,
        authorization: `Bearer ${SUPABASE_ANON_KEY}`,
      },
    });
    const json = await res.json().catch(() => null);
    if (!res.ok) {
      throw new Error((json as any)?.error || 'Failed to load KAMIS market prices');
    }
    return json as any;
  }

  async getKamisStatus() {
    if (!KAMIS_API_BASE_URL || !SUPABASE_ANON_KEY) {
      return { reachable: false, upstreamStatus: 0, upstreamError: 'Missing Supabase env', source: 'kamis.kilimo.go.ke' };
    }
    const url = `${KAMIS_API_BASE_URL.replace(/\/$/, '')}/status?_ts=${Date.now()}`;
    const res = await fetch(url, {
      headers: {
        apikey: SUPABASE_ANON_KEY,
        authorization: `Bearer ${SUPABASE_ANON_KEY}`,
      },
    });
    const json = await res.json().catch(() => null);
    if (!res.ok) throw new Error((json as any)?.error || 'Failed to check KAMIS status');
    return json as any;
  }

  private stripTags(s: string) {
    return s.replace(/<[^>]*>/g, ' ').replace(/\s+/g, ' ').trim();
  }

  private parsePriceCell(s: string) {
    const raw = this.stripTags(s);
    if (!raw || raw === '-' || raw === '—') return { price: null as number | null, unit: null as string | null };

    const normalized = raw.replace(/\u00A0/g, ' ').replace(/\s+/g, ' ').trim();

    const slashMatch = normalized.match(/([0-9][0-9,]*(?:\.[0-9]+)?)\s*(?:\/|per)\s*([A-Za-z0-9][A-Za-z0-9 ]{0,30})/i);
    if (slashMatch) {
      const n = Number(String(slashMatch[1]).replace(/,/g, ''));
      const unit = String(slashMatch[2]).trim().replace(/\s+/g, '');
      return Number.isFinite(n) ? { price: n, unit: unit || null } : { price: null, unit: unit || null };
    }

    const numberMatch = normalized.match(/([0-9][0-9,]*(?:\.[0-9]+)?)/);
    const n = numberMatch ? Number(String(numberMatch[1]).replace(/,/g, '')) : NaN;
    return Number.isFinite(n) ? { price: n, unit: null } : { price: null, unit: null };
  }

  async parseKamisExportFile(file: File) {
    const normalizeHeader = (s: string) =>
      s
        .toLowerCase()
        .replace(/\s+/g, ' ')
        .replace(/[()]/g, '')
        .trim();

    const matchesAny = (h: string, needles: string[]) => needles.some((n) => h === n || h.includes(n));

    const normalizeKamisDate = (raw: string) => {
      const trimmed = String(raw ?? '').trim();
      if (!trimmed) return null;

      const isoPrefix = trimmed.match(/^(\d{4}-\d{2}-\d{2})/);
      if (isoPrefix) return isoPrefix[1];

      if (/^\d{4}-\d{2}-\d{2}$/.test(trimmed)) return trimmed;

      const dmy = trimmed.match(/^(\d{1,2})[\/\-](\d{1,2})[\/\-](\d{4})$/);
      if (dmy) {
        const first = Number(dmy[1]);
        const second = Number(dmy[2]);
        const year = Number(dmy[3]);
        const day = first > 12 ? first : second > 12 ? second : first;
        const month = first > 12 ? second : second > 12 ? first : second;
        if (year >= 1900 && month >= 1 && month <= 12 && day >= 1 && day <= 31) {
          return `${String(year).padStart(4, '0')}-${String(month).padStart(2, '0')}-${String(day).padStart(2, '0')}`;
        }
      }

      const serial = trimmed.match(/^\d{5}$/);
      if (serial) {
        const excelDays = Number(serial[0]);
        if (Number.isFinite(excelDays) && excelDays > 20000 && excelDays < 90000) {
          const epochMs = (excelDays - 25569) * 86400 * 1000;
          const d = new Date(epochMs);
          if (!Number.isNaN(d.getTime())) {
            return `${d.getUTCFullYear()}-${String(d.getUTCMonth() + 1).padStart(2, '0')}-${String(d.getUTCDate()).padStart(2, '0')}`;
          }
        }
      }

      const parsed = new Date(trimmed);
      if (!Number.isNaN(parsed.getTime())) {
        return `${parsed.getUTCFullYear()}-${String(parsed.getUTCMonth() + 1).padStart(2, '0')}-${String(parsed.getUTCDate()).padStart(2, '0')}`;
      }

      return null;
    };

    const decodeCandidates = async () => {
      const candidates: string[] = [];
      try {
        candidates.push(await file.text());
      } catch {
      }
      try {
        const buf = await file.arrayBuffer();
        const encodings = ['utf-8', 'windows-1252', 'iso-8859-1'] as const;
        for (const enc of encodings) {
          try {
            candidates.push(new TextDecoder(enc as any).decode(buf));
          } catch {
          }
        }
      } catch {
      }
      const unique: string[] = [];
      for (const c of candidates) {
        if (!c) continue;
        if (!unique.includes(c)) unique.push(c);
      }
      return unique;
    };

    const gridFromHtml = (html: string) => {
      const doc = new DOMParser().parseFromString(html, 'text/html');
      const tables = Array.from(doc.querySelectorAll('table'));
      const pick =
        tables.find((t) => {
          const firstRow = t.querySelector('tr');
          const cells = firstRow ? Array.from(firstRow.querySelectorAll('th,td')).map((c) => normalizeHeader(this.stripTags(c.innerHTML))) : [];
          return cells.includes('commodity') && cells.includes('market') && cells.includes('date');
        }) ?? tables[0] ?? null;
      if (!pick) return null;
      const rows = Array.from(pick.querySelectorAll('tr')).map((tr) =>
        Array.from(tr.querySelectorAll('th,td')).map((c) => this.stripTags(c.innerHTML)),
      );
      return rows.length ? rows : null;
    };

    const childrenByLocalName = (el: Element, localName: string) =>
      Array.from(el.children).filter((c) => c.localName?.toLowerCase() === localName.toLowerCase());

    const getAttrAny = (el: Element, names: string[]) => {
      for (const n of names) {
        const v = el.getAttribute(n);
        if (v) return v;
      }
      return null;
    };

    const gridFromSpreadsheetXml = (xml: string) => {
      const doc = new DOMParser().parseFromString(xml, 'text/xml');
      const parseError = doc.querySelector('parsererror');
      if (parseError) return null;

      const allTables = Array.from(doc.getElementsByTagName('*')).filter((n) => (n as any)?.localName?.toLowerCase() === 'table') as Element[];
      const table = allTables[0] ?? null;
      if (!table) return null;

      const rowEls = Array.from(table.getElementsByTagName('*')).filter((n) => (n as any)?.localName?.toLowerCase() === 'row') as Element[];
      if (!rowEls.length) return null;

      const grid: string[][] = [];
      for (const rowEl of rowEls) {
        const cellEls = childrenByLocalName(rowEl, 'Cell');
        const row: string[] = [];
        let col = 1;
        for (const cellEl of cellEls) {
          const idxRaw = getAttrAny(cellEl, ['ss:Index', 'Index', 'index']);
          const idx = idxRaw ? Number(idxRaw) : NaN;
          if (Number.isFinite(idx) && idx > 0) col = idx;

          const dataEl =
            childrenByLocalName(cellEl, 'Data')[0] ??
            (Array.from(cellEl.getElementsByTagName('*')).find((n) => (n as any)?.localName?.toLowerCase() === 'data') as Element | undefined) ??
            null;
          const value = (dataEl?.textContent ?? '').trim();
          row[col - 1] = value;
          col += 1;
        }
        const anyValue = row.some((c) => typeof c === 'string' && c.trim().length > 0);
        if (anyValue) grid.push(row.map((c) => (c ?? '').trim()));
      }
      return grid.length ? grid : null;
    };

    const candidates = await decodeCandidates();
    let grid: string[][] | null = null;

    for (const c of candidates) {
      if (/<table\b/i.test(c)) {
        grid = gridFromHtml(c);
        if (grid) break;
      }
      if (/<(workbook|worksheet)\b/i.test(c) || /urn:schemas-microsoft-com:office:spreadsheet/i.test(c)) {
        grid = gridFromSpreadsheetXml(c);
        if (grid) break;
      }
    }

    if (!grid) {
      throw new Error('Unsupported KAMIS file format. Please upload the KAMIS "Export to Excel" file (HTML/Spreadsheet XML).');
    }

    const headerRowIndex = grid.findIndex((r) => {
      const headers = r.map((h) => normalizeHeader(String(h ?? '')));
      return headers.includes('commodity') && headers.includes('market') && headers.includes('date');
    });
    if (headerRowIndex < 0) throw new Error('Could not find header row (Commodity, Market, Date) in file.');

    const headerCells = grid[headerRowIndex].map((h) => normalizeHeader(String(h ?? '')));
    const getIdx = (names: string[]) => headerCells.findIndex((h) => matchesAny(h, names));
    const idxCommodity = getIdx(['commodity']);
    const idxClassification = getIdx(['classification']);
    const idxGrade = getIdx(['grade']);
    const idxSex = getIdx(['sex']);
    const idxMarket = getIdx(['market']);
    const idxWholesale = getIdx(['wholesale']);
    const idxRetail = getIdx(['retail']);
    const idxSupply = getIdx(['supply volume', 'supply']);
    const idxCounty = getIdx(['county']);
    const idxDate = getIdx(['date']);

    if (idxCommodity < 0 || idxMarket < 0 || idxDate < 0) {
      throw new Error('Missing required columns (Commodity, Market, Date) in file.');
    }

    const out: any[] = [];
    for (const r of grid.slice(headerRowIndex + 1)) {
      if (!r || r.length < 3) continue;
      const cell = (i: number) => (i >= 0 && i < r.length ? String(r[i] ?? '') : '');

      const commodity = cell(idxCommodity).trim();
      const market = cell(idxMarket).trim();
      const county = cell(idxCounty).trim();
      const dateRaw = cell(idxDate).trim();
      const priceDate = normalizeKamisDate(dateRaw) ?? '';
      if (!commodity || !market || !priceDate) continue;

      const wholesale = this.parsePriceCell(cell(idxWholesale));
      const retail = this.parsePriceCell(cell(idxRetail));
      const unit = wholesale.unit ?? retail.unit ?? null;

      const supplyRaw = cell(idxSupply).replace(/,/g, '').trim();
      const supplyVolume = supplyRaw && supplyRaw !== '-' ? Number(supplyRaw) : null;

      out.push({
        commodity,
        classification: cell(idxClassification).trim() || null,
        grade: cell(idxGrade).trim() || null,
        sex: cell(idxSex).trim() || null,
        market,
        county: county || null,
        wholesale_price: wholesale.price,
        retail_price: retail.price,
        unit,
        supply_volume: Number.isFinite(supplyVolume as any) ? supplyVolume : null,
        price_date: priceDate,
        source_url: KAMIS_EXPORT_URL,
      });
    }

    if (out.length === 0) throw new Error('No rows parsed from file');
    return out;
  }

  async ingestKamisMarketRows(rows: any[]) {
    if (!KAMIS_API_BASE_URL || !SUPABASE_ANON_KEY) throw new Error('Missing Supabase env');
    const { data } = await supabase.auth.getSession();
    const token = data.session?.access_token;
    if (!token) throw new Error('Login required');

    const url = `${KAMIS_API_BASE_URL.replace(/\/$/, '')}/ingest`;
    const chunkSize = 500;
    let insertedTotal = 0;

    for (let i = 0; i < rows.length; i += chunkSize) {
      const chunk = rows.slice(i, i + chunkSize);
      const res = await fetch(url, {
        method: 'POST',
        headers: {
          apikey: SUPABASE_ANON_KEY,
          authorization: `Bearer ${token}`,
          'content-type': 'application/json',
        },
        body: JSON.stringify({ rows: chunk }),
      });
      const json = await res.json().catch(() => null);
      if (!res.ok) throw new Error((json as any)?.error || 'Failed to ingest data');
      insertedTotal += Number((json as any)?.inserted ?? chunk.length) || 0;
    }

    return { inserted: insertedTotal } as any;
  }

  async getCountyByName(countyName: string): Promise<CountyData> {
    try {
      console.log(`Loading county data from backend API for ${countyName}...`);
      const apiCounty = await countyService.getCountyByName(countyName);
      return apiCounty;
    } catch (error) {
      console.error('Error loading county data from backend API:', error);
      throw new Error(
        'County profile is not available yet. Ask an admin to populate the Supabase counties dataset for this county before using county suitability insights.',
      );
    }
  }

  // Fetch crop data from Ministry of Agriculture
  async getCropData(cropName: string): Promise<CropData> {
    try {
      console.log(`Loading crop data from backend API for ${cropName}...`);
      const apiCrop = await countyService.getCropData(cropName);
      return apiCrop;
    } catch (error) {
      console.error('Error loading crop data from backend API:', error);
      throw new Error(
        'Crop profile is not available yet. Ask an admin to populate the Supabase crop encyclopedia dataset for this crop before using suitability insights.',
      );
    }
  }

  // Helper methods to generate realistic data
  private getRegionForCounty(countyName: string): string {
    const regions = {
      'Nairobi': 'Central',
      'Kiambu': 'Central',
      'Kisumu': 'Western',
      'Mombasa': 'Coastal',
      'Nakuru': 'Rift Valley'
    };
    return regions[countyName as keyof typeof regions] || 'Unknown';
  }

  private getTemperatureData(countyName: string): string {
    const temperatures = {
      'Nairobi': '12-28°C',
      'Kiambu': '10-25°C',
      'Kisumu': '17-30°C',
      'Mombasa': '22-33°C',
      'Nakuru': '8-26°C'
    };
    return temperatures[countyName as keyof typeof temperatures] || '15-30°C';
  }

  private getRainfallData(countyName: string): string {
    const rainfall = {
      'Nairobi': '800-1200mm annually',
      'Kiambu': '1000-1500mm annually',
      'Kisumu': '1100-1700mm annually',
      'Mombasa': '800-1100mm annually',
      'Nakuru': '900-1300mm annually'
    };
    return rainfall[countyName as keyof typeof rainfall] || '900-1400mm annually';
  }

  private getAltitudeData(countyName: string): string {
    const altitude = {
      'Nairobi': '1600-1850m above sea level',
      'Kiambu': '1700-2200m above sea level',
      'Kisumu': '1100-1300m above sea level',
      'Mombasa': '0-50m above sea level',
      'Nakuru': '1800-2100m above sea level'
    };
    return altitude[countyName as keyof typeof altitude] || '1000-2000m above sea level';
  }

  private getMainCropsForCounty(countyName: string): string[] {
    const crops = {
      'Nairobi': ['Vegetables', 'Potatoes', 'Beans'],
      'Kiambu': ['Coffee', 'Tea', 'Maize', 'Beans', 'Potatoes'],
      'Kisumu': ['Rice', 'Sugar Cane', 'Maize', 'Sweet Potatoes'],
      'Mombasa': ['Coconut', 'Cashew Nuts', 'Cassava', 'Maize'],
      'Nakuru': ['Wheat', 'Maize', 'Potatoes', 'Vegetables']
    };
    return crops[countyName as keyof typeof crops] || ['Maize', 'Beans', 'Wheat'];
  }

  private async fetchMarketPrices(countyName: string): Promise<any[]> {
    try {
      if (this.ncpbCache && Date.now() - this.ncpbCache.timestamp < 12 * 60 * 60 * 1000) {
        return this.ncpbCache.data;
      }

      const encodedUrl = encodeURIComponent(NCPB_PRICES_URL);
      const response = await axios.get(`${this.proxyUrl}${encodedUrl}`, {
        timeout: 15000
      });

      const html =
        typeof response.data === 'string'
          ? response.data
          : JSON.stringify(response.data);

      const doc = parseHTML(html, 'table');
      const tables = Array.from(doc.querySelectorAll('table'));
      const scrapedPrices: any[] = [];

      for (const table of tables) {
        const rows = Array.from(table.querySelectorAll('tr'));
        for (const row of rows) {
          const cells = Array.from(row.querySelectorAll('td'));
          if (cells.length < 2) {
            continue;
          }
          const commodityText = (cells[0].textContent || '').trim();
          if (!commodityText) {
            continue;
          }
          const lowerCommodity = commodityText.toLowerCase();
          if (
            !(
              lowerCommodity.includes('maize') ||
              lowerCommodity.includes('beans') ||
              lowerCommodity.includes('wheat') ||
              lowerCommodity.includes('rice') ||
              lowerCommodity.includes('sorghum') ||
              lowerCommodity.includes('barley')
            )
          ) {
            continue;
          }

          const priceText = (cells[1].textContent || '').trim();
          const numericPrice = parseFloat(priceText.replace(/[^0-9.-]+/g, ''));
          if (!numericPrice || Number.isNaN(numericPrice)) {
            continue;
          }

          const unitText =
            (cells[2] && cells[2].textContent) ?
              cells[2].textContent!.trim() :
              '90kg bag';

          scrapedPrices.push({
            commodity: commodityText,
            price: numericPrice,
            unit: unitText,
            date: new Date().toISOString().split('T')[0]
          });
        }
      }

      if (scrapedPrices.length > 0) {
        this.ncpbCache = {
          data: scrapedPrices,
          timestamp: Date.now()
        };
        return scrapedPrices;
      }
    } catch (error) {
      console.error('Error scraping NCPB prices, falling back to generated data:', error);
    }

    try {
      const basePrices = {
        'Maize': { price: 3500, unit: 'bag' },
        'Beans': { price: 8000, unit: 'bag' },
        'Potatoes': { price: 2500, unit: 'bag' },
        'Rice': { price: 120, unit: 'kg' },
        'Sugar Cane': { price: 4000, unit: 'ton' },
        'Coffee': { price: 350, unit: 'kg' },
        'Tea': { price: 250, unit: 'kg' },
        'Wheat': { price: 3200, unit: 'bag' },
        'Sweet Potatoes': { price: 1800, unit: 'bag' },
        'Vegetables': { price: 1500, unit: 'crate' }
      };

      // Generate market prices based on county and add variation
      return this.getMainCropsForCounty(countyName).map(crop => {
        const baseInfo = basePrices[crop as keyof typeof basePrices] || { price: 2000, unit: 'unit' };
        // Add random variation to prices
        const variation = Math.floor(Math.random() * 300) - 150;
        
        return {
          commodity: crop,
          price: baseInfo.price + variation,
          unit: baseInfo.unit,
          date: new Date().toISOString().split('T')[0]
        };
      });
    } catch (error) {
      console.error('Error fetching market prices:', error);
      return [];
    }
  }

  private getFarmingTipsForCounty(countyName: string): any[] {
    // Return county-specific farming tips
    const commonTips = [
      {
        category: 'Soil Management',
        tips: [
          'Test soil before planting to determine nutrient levels',
          'Practice crop rotation to maintain soil fertility',
          'Use appropriate fertilizers based on soil test results'
        ]
      },
      {
        category: 'Water Management',
        tips: [
          'Implement water harvesting techniques during rainy seasons',
          'Consider drip irrigation for efficient water use',
          'Mulch around plants to reduce water evaporation'
        ]
      }
    ];

    const countySpecificTips = {
      'Kiambu': [
        {
          category: 'Coffee Farming',
          tips: [
            'Prune coffee trees after harvesting',
            'Control coffee berry disease through regular spraying',
            'Maintain shade trees at appropriate density'
          ]
        }
      ],
      'Kisumu': [
        {
          category: 'Rice Cultivation',
          tips: [
            'Prepare paddy fields properly before planting',
            'Maintain appropriate water levels throughout growing season',
            'Control weeds early to prevent competition with rice plants'
          ]
        }
      ],
      'Mombasa': [
        {
          category: 'Coastal Farming',
          tips: [
            'Select salt-tolerant crop varieties',
            'Create windbreaks to protect crops from strong coastal winds',
            'Implement erosion control measures during heavy rains'
          ]
        }
      ]
    };

    return [
      ...commonTips,
      ...(countySpecificTips[countyName as keyof typeof countySpecificTips] || [])
    ];
  }

  private getGrowingConditions(cropName: string): Record<string, string> {
    const conditions = {
      'Maize': {
        temperature: '18-30°C',
        rainfall: '500-1000mm per growing season',
        soil: 'Well-drained loamy or sandy loam soils',
        growing_period: '90-120 days'
      },
      'Coffee': {
        temperature: '15-25°C',
        rainfall: '1500-2000mm annually',
        soil: 'Well-drained, rich in organic matter, pH 5.0-6.0',
        growing_period: 'Perennial, first harvest after 3-4 years'
      },
      'Tea': {
        temperature: '18-25°C',
        rainfall: '1500-2500mm, well-distributed',
        soil: 'Deep, well-drained, acidic soils (pH 4.5-5.5)',
        growing_period: 'Perennial, first harvest after 3 years'
      },
      'Rice': {
        temperature: '20-35°C',
        rainfall: '1000-1500mm',
        soil: 'Clay or clay loam with good water retention',
        growing_period: '90-150 days depending on variety'
      }
    };
    
    return conditions[cropName as keyof typeof conditions] || {
      temperature: '15-30°C',
      rainfall: '800-1200mm',
      soil: 'Well-drained loamy soil',
      growing_period: '90-120 days'
    };
  }

  private getBestPractices(cropName: string): string[] {
    const practices = {
      'Maize': [
        'Plant at the onset of consistent rainfall',
        'Space rows 75cm apart, plants 25-30cm within rows',
        'Apply nitrogen fertilizer in split applications',
        'Control fall armyworm through regular monitoring and spraying',
        'Harvest when kernels are dry and hard'
      ],
      'Coffee': [
        'Maintain proper spacing (2m x 2m for Arabica)',
        'Prune regularly to maintain productivity',
        'Apply organic mulch around the base of plants',
        'Control coffee berry disease and leaf rust through spraying',
        'Harvest only ripe cherries for best quality'
      ],
      'Tea': [
        'Maintain hedges at appropriate height through pruning',
        'Pluck two leaves and a bud for best quality',
        'Apply nitrogen fertilizer regularly for leaf production',
        'Control pests like thrips through integrated pest management',
        'Maintain good drainage in tea fields'
      ],
      'Rice': [
        'Prepare seedbeds thoroughly before transplanting',
        'Maintain appropriate water levels throughout growing season',
        'Apply fertilizer at critical growth stages',
        'Control weeds early in the growing season',
        'Drain fields 10-15 days before harvesting'
      ]
    };
    
    return practices[cropName as keyof typeof practices] || [
      'Ensure proper soil preparation before planting',
      'Apply appropriate fertilizers based on soil tests',
      'Practice integrated pest management',
      'Harvest at optimal maturity for best quality',
      'Implement proper post-harvest handling'
    ];
  }

  private async fetchCountyPerformance(cropName: string): Promise<Record<string, number>> {
    // Simulate fetching county performance data for the crop
    try {
      // In a real implementation, we would fetch from an API or scrape a website
      // For demonstration, generate realistic data
      const counties = ['Nairobi', 'Kiambu', 'Kisumu', 'Mombasa', 'Nakuru'];
      const performance: Record<string, number> = {};
      
      counties.forEach(county => {
        // Generate a score between 30 and 95
        const baseScore = {
          'Maize': { 'Kiambu': 80, 'Nakuru': 85, 'Kisumu': 70 },
          'Coffee': { 'Kiambu': 90, 'Nakuru': 70, 'Nairobi': 50 },
          'Tea': { 'Kiambu': 88, 'Kisumu': 45, 'Nakuru': 60 },
          'Rice': { 'Kisumu': 85, 'Mombasa': 65, 'Nakuru': 50 }
        };
        
        const baseScoreForCrop = baseScore[cropName as keyof typeof baseScore] || {};
        const baseValue = baseScoreForCrop[county as keyof typeof baseScoreForCrop] || Math.floor(Math.random() * 40) + 40;
        
        // Add some randomness (±5)
        performance[county] = Math.min(95, Math.max(30, baseValue + (Math.floor(Math.random() * 11) - 5)));
      });
      
      return performance;
    } catch (error) {
      console.error('Error fetching county performance data:', error);
      return { 'Kiambu': 75, 'Nairobi': 60, 'Kisumu': 70 };
    }
  }

  private async fetchMarketTrend(cropName: string): Promise<any[]> {
    // Simulate fetching market trend data for the crop
    try {
      // In a real implementation, we would fetch from an API or scrape a website
      const months = ['January', 'February', 'March', 'April', 'May', 'June', 
                      'July', 'August', 'September', 'October', 'November', 'December'];
      
      // Base prices for different crops
      const basePrices = {
        'Maize': 3500,
        'Coffee': 350,
        'Tea': 250,
        'Rice': 120,
        'Wheat': 3200,
        'Beans': 8000,
        'Potatoes': 2500
      };
      
      // Get base price or default to 2000
      const basePrice = basePrices[cropName as keyof typeof basePrices] || 2000;
      
      // Season patterns - different crops have different seasonal patterns
      const seasonPatterns = {
        'Maize': [0.9, 0.8, 0.85, 0.95, 1.1, 1.2, 1.1, 1.0, 0.9, 0.85, 0.9, 1.0],
        'Coffee': [1.0, 1.05, 1.1, 1.15, 1.2, 1.1, 1.0, 0.95, 0.9, 0.95, 1.0, 1.05],
        'Tea': [1.1, 1.15, 1.0, 0.9, 0.85, 0.8, 0.85, 0.9, 0.95, 1.0, 1.05, 1.1],
        'Rice': [0.95, 1.0, 1.05, 1.1, 1.15, 1.1, 1.0, 0.9, 0.85, 0.9, 0.95, 1.0]
      };
      
      // Get season pattern or default to flat with some variation
      const pattern = seasonPatterns[cropName as keyof typeof seasonPatterns] || 
        months.map(() => 0.9 + Math.random() * 0.3);
      
      // Generate price trend with seasonal patterns
      return months.map((month, index) => {
        // Apply seasonal pattern and add some random noise
        const seasonalFactor = pattern[index];
        const randomNoise = 0.95 + Math.random() * 0.1; // ±5% random variation
        const price = Math.round(basePrice * seasonalFactor * randomNoise);
        
        return {
          month,
          price
        };
      });
    } catch (error) {
      console.error('Error fetching market trend data:', error);
      // Return fallback data
      return [
        { month: 'January', price: 3500 },
        { month: 'February', price: 3400 },
        { month: 'March', price: 3450 },
        { month: 'April', price: 3600 },
        { month: 'May', price: 3800 },
        { month: 'June', price: 4000 },
        { month: 'July', price: 3900 },
        { month: 'August', price: 3700 },
        { month: 'September', price: 3500 },
        { month: 'October', price: 3400 },
        { month: 'November', price: 3500 },
        { month: 'December', price: 3600 }
      ];
    }
  }

  private getNationalAverage(cropName: string): number {
    const averages = {
      'Maize': 70,
      'Coffee': 75,
      'Tea': 72,
      'Rice': 68,
      'Wheat': 65
    };
    return averages[cropName as keyof typeof averages] || 65;
  }

  private async fetchInternationalData(cropName: string): Promise<Record<string, number>> {
    // Simulate fetching international comparison data
    try {
      // In a real implementation, we would fetch from FAO or other international data sources
      const baseComparisons = {
        'Maize': {
          'Kenya': 65,
          'Uganda': 60,
          'Tanzania': 58,
          'South Africa': 85,
          'USA': 92
        },
        'Coffee': {
          'Kenya': 82,
          'Ethiopia': 88,
          'Colombia': 90,
          'Brazil': 85,
          'Vietnam': 75
        },
        'Tea': {
          'Kenya': 85,
          'India': 88,
          'Sri Lanka': 82,
          'China': 90,
          'Japan': 92
        },
        'Rice': {
          'Kenya': 60,
          'Tanzania': 65,
          'Egypt': 80,
          'India': 85,
          'China': 88
        }
      };
      
      return baseComparisons[cropName as keyof typeof baseComparisons] || {
        'Kenya': 65,
        'Tanzania': 62,
        'Uganda': 60,
        'South Africa': 75,
        'Global Average': 80
      };
    } catch (error) {
      console.error('Error fetching international comparison data:', error);
      return {
        'Kenya': 65,
        'East Africa': 60,
        'Africa': 55,
        'Global': 75
      };
    }
  }

  /**
   * Scrape Kenya's trade data from the Kenya National Bureau of Statistics website
   * @param year The year to fetch data for
   */
  async scrapeKenyaTradeData(year: number): Promise<ImportExportAnalysis | null> {
    try {
      const comtradeUrl = `https://comtrade.un.org/api/get?type=C&freq=A&px=HS&ps=${year}&r=404&p=0&rg=all&cc=TOTAL&fmt=json`;
      const encodedUrl = encodeURIComponent(comtradeUrl);
      const response = await axios.get(`${this.proxyUrl}${encodedUrl}`, {
        timeout: 20000
      });

      const rawData =
        typeof response.data === 'string'
          ? JSON.parse(response.data)
          : response.data;

      const dataset = Array.isArray(rawData.dataset) ? rawData.dataset : [];

      if (!dataset.length) {
        return null;
      }

      let totalExports = 0;
      let totalImports = 0;
      const topExports: ExportData[] = [];
      const topImports: ImportData[] = [];

      dataset.forEach((item: any) => {
        if (item.rgDesc === 'Export') {
          totalExports += item.TradeValue;
          topExports.push({
            destination: item.ptTitle,
            quantity: item.qty || 0,
            value: item.TradeValue,
            unit: item.qtDesc || 'kg',
            timestamp: String(item.Period) || String(year)
          });
        } else if (item.rgDesc === 'Import') {
          totalImports += item.TradeValue;
          topImports.push({
            source: item.ptTitle,
            quantity: item.qty || 0,
            value: item.TradeValue,
            unit: item.qtDesc || 'kg',
            timestamp: String(item.Period) || String(year)
          });
        }
      });

      topExports.sort((a, b) => b.value - a.value);
      topImports.sort((a, b) => b.value - a.value);

      const analysis: ImportExportAnalysis = {
        totalExportValue: totalExports,
        totalImportValue: totalImports,
        tradeBalance: totalExports - totalImports,
        yearOnYearGrowth: null,
        tradeVolume: totalExports + totalImports,
        topExports: topExports.slice(0, 10).map(item => ({
          commodity: 'All commodities',
          destination: item.destination,
          value: item.value,
          quantity: item.quantity
        })),
        topImports: topImports.slice(0, 10).map(item => ({
          commodity: 'All commodities',
          source: item.source,
          value: item.value,
          quantity: item.quantity
        })),
        regulations: [],
        dataSource: 'UN Comtrade Database (via proxy)'
      };

      return analysis;

    } catch (error) {
      console.error('Error scraping Kenya trade data:', error);
      return null;
    }
  }
  
  /**
   * Scrape commodity trade data from Trade Map or other accessible sources
   * @param commodity The commodity to fetch data for
   * @param year The year to fetch data for
   */
  async scrapeCommodityData(commodity: string, year: number): Promise<{exports: ExportData[], imports: ImportData[]} | null> {
    try {
      console.log(`Simulating scraping commodity data for ${commodity} from OEC website...`);
      await new Promise(resolve => setTimeout(resolve, 1200));

      // Generate realistic data based on commodity
      const multiplier = (commodity.length + year) % 5 + 1;
      
      return {
        exports: [
          { destination: "UK", quantity: 50000 * multiplier, value: 150000000 * multiplier, unit: "kg", timestamp: new Date().toISOString() },
          { destination: "Netherlands", quantity: 45000 * multiplier, value: 135000000 * multiplier, unit: "kg", timestamp: new Date().toISOString() },
          { destination: "Pakistan", quantity: 40000 * multiplier, value: 120000000 * multiplier, unit: "kg", timestamp: new Date().toISOString() },
          { destination: "USA", quantity: 30000 * multiplier, value: 90000000 * multiplier, unit: "kg", timestamp: new Date().toISOString() },
          { destination: "Egypt", quantity: 25000 * multiplier, value: 75000000 * multiplier, unit: "kg", timestamp: new Date().toISOString() }
        ],
        imports: [
          { source: "China", quantity: 10000 * multiplier, value: 50000000 * multiplier, unit: "kg", timestamp: new Date().toISOString() },
          { source: "India", quantity: 8000 * multiplier, value: 40000000 * multiplier, unit: "kg", timestamp: new Date().toISOString() },
          { source: "UAE", quantity: 6000 * multiplier, value: 30000000 * multiplier, unit: "kg", timestamp: new Date().toISOString() }
        ]
      };
    } catch (error) {
      console.error(`Error scraping commodity data for ${commodity}:`, error);
      return null;
    }
  }
  
  /**
   * Scrape trade regulations from Kenya Revenue Authority website
   */
  async scrapeTradeRegulations(): Promise<TradeRegulation[] | null> {
    try {
      // Use a proxy to access regulations
      const encodedUrl = encodeURIComponent('https://www.kra.go.ke/customs/import-export');
      const response = await axios.get(`${this.proxyUrl}${encodedUrl}`, {
        timeout: 15000
      });
      
      console.log('Scraped trade regulations from KRA website');
      
      // In a real implementation, we would parse the HTML to extract regulations
      // For now, we'll return null to indicate scraping wasn't successful
      return null;
    } catch (error) {
      console.error('Error scraping trade regulations:', error);
      return null;
    }
  }
}

export const webScrapingService = new WebScrapingService(); 
